import { z } from "zod";

import {
  AgenticLogicDatasetJsonSchemaSchema,
  AgenticLogicDatasetKeySchema,
} from "../../../dtos/agentic-logic/datasets";
import { AgenticLogicSourceNameSchema } from "../../../dtos/agentic-logic/sources";
import { AgenticLogicSdkReadResultSchema } from "../sdk";

import { AgenticLogicSdkReadOperationSchema } from "./sdk-read";

// ---------------------------------------------------------------------------
// The publish-time READ MANIFEST, and the seeded reads serving derives from it.
// ---------------------------------------------------------------------------

/**
 * The most reads one version prefetches. Each is one Upstash key the serving
 * broker fetches before every evaluation, so the cap bounds that fetch, not
 * the rule — and not the dependency record, which is uncapped.
 */
export const AGENTIC_LOGIC_MAX_PREFETCH_READS = 16;

/**
 * The most response bytes the broker seeds into one invocation payload. Well
 * under the 6 MB synchronous Lambda payload limit, which the invocation and
 * the request it carries share; a hit that would cross it is simply not
 * seeded, and the rule reads it through the SDK-read Lambda as before.
 */
export const AGENTIC_LOGIC_MAX_SEEDED_READ_BYTES = 1_000_000;

/**
 * What a published version reads whatever request it serves, recorded at
 * publish. Two jobs, kept apart so neither limits the other:
 *
 *  - `prefetch`: the reads serving answers from the shared caches before the
 *    rule runs — full SDK operations, so the trusted side can recompute each
 *    one's cache key and request hash and re-run it when the cached answer is
 *    stale. Capped (see {@link AGENTIC_LOGIC_MAX_PREFETCH_READS}).
 *  - `dependencies`: EVERY dataset key and Source name the version reads, in
 *    any validation run. Delete refusals, schema pins and Source warm-up read
 *    this, so it is never truncated.
 */
export const AgenticLogicReadManifestSchema = z
  .object({
    schemaVersion: z.literal(2),
    prefetch: z
      .array(AgenticLogicSdkReadOperationSchema)
      .max(AGENTIC_LOGIC_MAX_PREFETCH_READS),
    dependencies: z
      .object({
        datasets: z.array(AgenticLogicDatasetKeySchema),
        sources: z.array(AgenticLogicSourceNameSchema),
      })
      .strict(),
    /**
     * The JSON Schema of every dataset this version reads, keyed by dataset
     * key, as stored when the version was published — the version's SCHEMA
     * PINS (see the dataset contract in dtos/agentic-logic/datasets). Absent
     * when the version reads no stored dataset.
     */
    datasetSchemas: z
      .record(AgenticLogicDatasetKeySchema, AgenticLogicDatasetJsonSchemaSchema)
      .optional(),
  })
  .strict();
export type AgenticLogicReadManifest = z.infer<
  typeof AgenticLogicReadManifestSchema
>;

/**
 * One read the serving broker answered BEFORE the rule ran, from the shared
 * read cache: the SDK serves a read whose request hash matches from this
 * instead of invoking the SDK-read Lambda. Exactly the recorded-read shape a
 * live read returns, so a seeded answer is indistinguishable to the rule.
 */
export const AgenticLogicSeededReadSchema = AgenticLogicSdkReadResultSchema;
export type AgenticLogicSeededRead = z.infer<
  typeof AgenticLogicSeededReadSchema
>;
